import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Flex, Heading, Text, VStack, Image, Icon,
  Link, useColorModeValue, Container, HStack,
} from "@chakra-ui/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { FaMapMarkerAlt, FaArrowRight, FaArrowLeft } from "react-icons/fa";

// Assets
import ArunaImg     from "../../assets/images/private/ArunaKumari.png";
import EshwarImg    from "../../assets/images/private/EshwarNagaraj.png";
import SruthiImg    from "../../assets/images/private/SruthiPasupuletiGrpTeam.png";
import ChaitanyaImg from "../../assets/images/private/ChaitanyaReddy.png";
import ArunImg      from "../../assets/images/private/ArunPatil.png";
import MuraliImg    from "../../assets/images/private/MuraliKumar.png";
import SowmyaImg    from "../../assets/images/private/SowmyaRanjan.png";

/* ─────────────────────────────────────────
   PEERLIST ICON
───────────────────────────────────────── */
const PeerlistIcon = (props) => (
  <Icon viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M12 0C2.667 0 0 2.667 0 12s2.673 12 12 12 12-2.667 12-12S21.327 0 12 0zm8.892 20.894c-1.57 1.569-4.247 2.249-8.892 2.249s-7.322-.68-8.892-2.25C1.735 19.522 1.041 17.3.89 13.654A39.74 39.74 0 0 1 .857 12c0-1.162.043-2.201.13-3.13.177-1.859.537-3.278 1.106-4.366.284-.544.62-1.006 1.013-1.398s.854-.729 1.398-1.013C5.592 1.524 7.01 1.164 8.87.988 9.799.9 10.838.858 12 .858c4.645 0 7.322.68 8.892 2.248 1.569 1.569 2.25 4.246 2.25 8.894s-.681 7.325-2.25 8.894zM14.25 16.49a6.097 6.097 0 0 1-2.442.59v2.706H10.45v.357H6.429V5.57h.357V4.214h5.676c3.565 0 6.467 2.81 6.467 6.262 0 2.852-1.981 5.26-4.68 6.013zm-1.788-8.728H10.45v5.428h2.011c1.532 0 2.802-1.2 2.802-2.714s-1.27-2.714-2.802-2.714z"
      fill="currentColor"
    />
  </Icon>
);

/* ─────────────────────────────────────────
   TEAM DATA
───────────────────────────────────────── */
const teamMembers = [
  {
    name: "Eshwar Nagaraj",
    role: "Founder",
    company: "GrapplTech · OneDayCoder",
    location: "Chennai, Tamil Nadu",
    image: EshwarImg,
    peerlist: "https://peerlist.io/eshwarnagaraj",
    tag: "Founder",
    tagColor: "#028EC4",
    story: "GrapplTech was built on a clear vision — learning should translate into real capability. I wanted to create a system where execution matters more than consumption. Every step we take focuses on outcomes, not just content. We are building an ecosystem that turns learners into confident professionals.",
  },
  {
    name: "Aruna Kumari",
    role: "CSM",
    company: "GrapplTech · Capgemini",
    location: "Salem, Tamil Nadu",
    image: ArunaImg,
    peerlist: "https://peerlist.io/aruna",
    tag: "Operations",
    tagColor: "#7C3AED",
    story: "Joining GrapplTech meant contributing to something purposeful from day one. My focus is ensuring operations run seamlessly while keeping learners at the center. Every process is designed to support clarity, confidence, and growth. We are building trust through consistent and meaningful experiences.",
  },
  {
    name: "Sruthi Pasupuleti",
    role: "Product Design",
    company: "GrapplTech",
    location: "Anantapur, India",
    image: SruthiImg,
    peerlist: "https://peerlist.io/sruthi",
    tag: "Design",
    tagColor: "#DB2777",
    story: "Design here is about enabling outcomes, not just visuals. I focus on creating intuitive experiences that remove friction in learning. Every interface is built to guide users with clarity and purpose. The goal is to make every interaction feel simple, engaging, and impactful.",
  },
  {
    name: "Chaitanya Reddy",
    role: "Senior Data Engineer",
    company: "Expedia Group",
    location: "Bangalore, India",
    image: ChaitanyaImg,
    peerlist: "https://peerlist.io/chaitanya",
    tag: "Data",
    tagColor: "#059669",
    story: "GrapplTech bridges the gap between theory and real-world data problems. My role is to bring practical datasets and industry scenarios into learning. This helps learners understand how systems actually operate at scale. We are preparing them for challenges they will face in real engineering environments.",
  },
  {
    name: "Arun Patil",
    role: "Automation Test Analyst",
    company: "Cognizant",
    location: "Ontario, Canada",
    image: ArunImg,
    peerlist: "https://peerlist.io/arunpatil",
    tag: "QA",
    tagColor: "#D97706",
    story: "Quality is about reliability and real-world impact. I focus on helping learners understand how systems behave under different conditions. This ensures they move beyond theory into practical problem-solving. We emphasize building skills that directly translate into industry-ready expertise.",
  },
  {
    name: "Murali Kumar",
    role: "GenAI Engineer",
    company: "Cognizant",
    location: "Chennai, India",
    image: MuraliImg,
    peerlist: "https://peerlist.io/murali",
    tag: "AI/ML",
    tagColor: "#0891B2",
    story: "AI is evolving rapidly, and understanding it requires both clarity and responsibility. I focus on simplifying complex GenAI concepts into practical learning paths. This helps learners build strong intuition while staying grounded in real use cases. We aim to prepare them for an AI-driven future.",
  },
  {
    name: "Sowmya Ranjan",
    role: "Senior .Net Developer",
    company: "Cognizant",
    location: "Toronto, Canada",
    image: SowmyaImg,
    peerlist: "https://peerlist.io/sowmya",
    tag: "Engineering",
    tagColor: "#16A34A",
    story: "Engineering at GrapplTech is about building scalable and reliable systems. I focus on writing clean, maintainable code and encouraging best practices. Beyond development, it's about fostering ownership and continuous learning. We are creating a culture that drives both performance and growth.",
  },
];

const AUTO_PLAY_MS = 10000;
const TOTAL = teamMembers.length;

/* ─────────────────────────────────────────
   ANIMATED INDEX  (no layout shift — 
   uses overflow:hidden clip on a fixed box)
───────────────────────────────────────── */
const IndexDisplay = ({ current, total }) => (
  <HStack spacing={1} align="baseline">
    <Box overflow="hidden" h="52px" display="flex" alignItems="center">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={current}
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0,  opacity: 1 }}
          exit={{   y: -24, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          style={{
            fontFamily: "Bricolage Grotesque, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(28px, 4vw, 44px)",
            lineHeight: 1,
            display: "inline-block",
          }}
        >
          {String(current + 1).padStart(2, "0")}
        </motion.span>
      </AnimatePresence>
    </Box>
    <Text
      fontSize="sm" fontWeight="600" opacity={0.35}
      fontFamily="Bricolage Grotesque, sans-serif"
    >
      /{String(total).padStart(2, "0")}
    </Text>
  </HStack>
);

/* ─────────────────────────────────────────
   AVATAR THUMB STRIP
───────────────────────────────────────── */
const ThumbStrip = ({ members, activeIdx, onSelect }) => (
  <HStack spacing={2} flexWrap="wrap" justify={{ base: "center", lg: "flex-start" }}>
    {members.map((m, i) => (
      <Box
        key={i}
        as="button"
        onClick={() => onSelect(i)}
        flexShrink={0}
        position="relative"
        style={{ transition: "transform 0.25s ease", transform: activeIdx === i ? "scale(1.14)" : "scale(1)" }}
      >
        <Box
          w={{ base: "38px", md: "46px" }}
          h={{ base: "38px", md: "46px" }}
          rounded="full"
          overflow="hidden"
          style={{
            border: `2.5px solid ${activeIdx === i ? m.tagColor : "transparent"}`,
            boxShadow: activeIdx === i ? `0 0 0 3px ${m.tagColor}30` : "none",
            transition: "border-color 0.25s ease, box-shadow 0.25s ease",
          }}
        >
          <Image src={m.image} alt={m.name} w="full" h="full" objectFit="cover" />
        </Box>
        {activeIdx === i && (
          <Box
            position="absolute" bottom="-5px" left="50%"
            style={{ transform: "translateX(-50%)" }}
            w="4px" h="4px" rounded="full" bg={m.tagColor}
          />
        )}
      </Box>
    ))}
  </HStack>
);

/* ─────────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────────── */
const ProgressBar = ({ isPaused, duration, color, resetKey }) => (
  <Box
    h="2px" w="full" rounded="full" overflow="hidden"
    bg={useColorModeValue("gray.100", "whiteAlpha.100")}
  >
    <motion.div
      key={resetKey}
      initial={{ width: "0%" }}
      animate={{ width: isPaused ? "0%" : "100%" }}
      transition={{ duration: duration / 1000, ease: "linear" }}
      style={{ height: "100%", background: color, borderRadius: 9999 }}
    />
  </Box>
);

/* ─────────────────────────────────────────
   DECORATIVE GRID LINES
───────────────────────────────────────── */
const GridLines = () => (
  <Box position="absolute" inset={0} pointerEvents="none" zIndex={0} overflow="hidden">
    {[20, 40, 60, 80].map((p) => (
      <Box key={`h${p}`} position="absolute" left={0} right={0} top={`${p}%`}
        h="1px" bg="#028EC4" opacity={0.04} />
    ))}
    {[20, 40, 60, 80].map((p) => (
      <Box key={`v${p}`} position="absolute" top={0} bottom={0} left={`${p}%`}
        w="1px" bg="#028EC4" opacity={0.04} />
    ))}
    {/* corner brackets */}
    {[
      { top: "24px", left: "24px",  borderTop: "2px solid", borderLeft:  "2px solid" },
      { top: "24px", right: "24px", borderTop: "2px solid", borderRight: "2px solid" },
      { bottom: "24px", left: "24px",  borderBottom: "2px solid", borderLeft:  "2px solid" },
      { bottom: "24px", right: "24px", borderBottom: "2px solid", borderRight: "2px solid" },
    ].map((s, i) => (
      <Box key={i} position="absolute" w="24px" h="24px" borderColor="#028EC4" opacity={0.14} {...s} />
    ))}
  </Box>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const TeamSlider = () => {
  const [idx,      setIdx]    = useState(0);
  const [dir,      setDir]    = useState(1);
  const [isPaused, setPaused] = useState(false);

  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  /* touch / swipe state */
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const isLight    = useColorModeValue(true, false);
  const mainBg     = useColorModeValue("#F7F9FC", "#050810");
  const panelBg    = useColorModeValue("white", "#0D1117");
  const headingCol = useColorModeValue("gray.900", "white");
  const textBody   = useColorModeValue("gray.700", "gray.300");
  const textMuted  = useColorModeValue("gray.500", "gray.500");
  const borderCol  = useColorModeValue("gray.100", "whiteAlpha.50");
  const btnBorder  = useColorModeValue("gray.200", "whiteAlpha.100");

  const member = teamMembers[idx];

  /* navigation */
  const navigate = useCallback((newIdx) => {
    const resolvedIdx = ((newIdx % TOTAL) + TOTAL) % TOTAL;
    setDir(resolvedIdx === (idx + 1) % TOTAL || (idx === TOTAL - 1 && resolvedIdx === 0) ? 1 : -1);
    setIdx(resolvedIdx);
  }, [idx]);

  const next = useCallback(() => { setDir(1);  setIdx((p) => (p + 1) % TOTAL); }, []);
  const prev = useCallback(() => { setDir(-1); setIdx((p) => (p - 1 + TOTAL) % TOTAL); }, []);

  /* autoplay */
  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(next, AUTO_PLAY_MS);
    return () => clearInterval(t);
  }, [next, isPaused]);

  /* touch handlers — swipe left/right for mobile infinite scroll */
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // only register horizontal swipes (ignore scrolls)
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      dx < 0 ? next() : prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  /* ── animation variants ──
     KEY FIX: both enter/exit use opacity + x only.
     NO scale. The motion.div uses position:absolute + inset:0
     so it NEVER affects layout — eliminates the "big card" glitch.
  */
  const slideVariants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    show:  { opacity: 1, x: 0 },
    exit:  (d) => ({ opacity: 0, x: d > 0 ? -60 : 60 }),
  };

  const textVariants = {
    enter: (d) => ({ opacity: 0, y: d > 0 ? 20 : -20 }),
    show:  { opacity: 1, y: 0 },
    exit:  (d) => ({ opacity: 0, y: d > 0 ? -20 : 20 }),
  };

  const ease = [0.25, 0.46, 0.45, 0.94];

  return (
    <Box
      ref={ref}
      bg={mainBg}
      position="relative"
      overflow="hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <GridLines />

      <Container maxW="7xl" py={{ base: 16, md: 24 }} position="relative" zIndex={1}>

        {/* ── HEADER ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <HStack
            mb={{ base: 10, md: 14 }}
            justify="space-between"
            align="flex-end"
            flexWrap="wrap"
            gap={4}
          >
            <VStack align="flex-start" spacing={2}>
              <HStack spacing={2}>
                <Box w="28px" h="2px" bg="#028EC4" rounded="full" />
                <Text
                  fontSize="11px" fontWeight="800"
                  letterSpacing="0.22em" textTransform="uppercase"
                  color="#028EC4"
                >
                  The Team
                </Text>
              </HStack>
              <Heading
                fontSize={{ base: "3xl", md: "5xl" }}
                fontWeight="900"
                fontFamily="Bricolage Grotesque, sans-serif"
                lineHeight="1" letterSpacing="-2px"
                color={headingCol}
              >
                Hearts &amp; Minds <br />
                <Box as="span" color="#028EC4">Behind GrapplTech</Box>
              </Heading>
            </VStack>

            {/* thumb strip — desktop */}
            <Box display={{ base: "none", lg: "block" }}>
              <ThumbStrip members={teamMembers} activeIdx={idx} onSelect={navigate} />
            </Box>
          </HStack>
        </motion.div>

        {/* ── MAIN CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <Box
            rounded={{ base: "2xl", md: "3xl" }}
            border="1px solid"
            borderColor={borderCol}
            shadow={isLight ? "0 24px 64px rgba(0,0,0,0.08)" : "0 24px 64px rgba(0,0,0,0.5)"}
            bg={panelBg}
            overflow="hidden"
          >
            <Flex direction={{ base: "column", lg: "row" }}>

              {/* ══ PHOTO PANEL ══
                  Fixed height. overflow:hidden clips the absolute motion.div.
                  motion.div fills the clip — no layout shifts possible. */}
              <Box
                w={{ base: "100%", lg: "44%" }}
                h={{ base: "300px", sm: "380px", md: "440px", lg: "580px" }}
                position="relative"
                overflow="hidden"
                flexShrink={0}
              >
                <AnimatePresence mode="wait" custom={dir} initial={false}>
                  <motion.div
                    key={`photo-${idx}`}
                    custom={dir}
                    variants={slideVariants}
                    initial="enter"
                    animate="show"
                    exit="exit"
                    transition={{ duration: 0.5, ease }}
                    style={{
                      position: "absolute",
                      inset: 0,           /* fills parent exactly — zero layout impact */
                      willChange: "transform, opacity",
                    }}
                  >
                    <Image
                      src={member.image}
                      alt={member.name}
                      w="100%" h="100%"
                      objectFit="cover"
                      objectPosition="top center"
                      draggable={false}
                    />

                    {/* gradient: right edge blends into content panel */}
                    <Box
                      position="absolute" inset={0}
                      style={{
                        background: `linear-gradient(to right, transparent 55%, ${panelBg} 100%),
                                     linear-gradient(to top, ${panelBg} 0%, transparent 35%)`,
                      }}
                    />

                    {/* tag pill */}
                    <Box
                      position="absolute" top={5} left={5}
                      px={3} py={1.5} rounded="full"
                      fontSize="10px" fontWeight="800"
                      letterSpacing="0.12em" textTransform="uppercase"
                      style={{
                        background: `${member.tagColor}22`,
                        border: `1.5px solid ${member.tagColor}55`,
                        color: member.tagColor,
                        backdropFilter: "blur(8px)",
                      }}
                    >
                      {member.tag}
                    </Box>
                  </motion.div>
                </AnimatePresence>

                {/* index — sits above the AnimatePresence layer */}
                <Box
                  position="absolute" bottom={5} left={5} zIndex={2}
                  color={isLight ? "gray.800" : "white"}
                  style={{ pointerEvents: "none" }}
                >
                  <IndexDisplay current={idx} total={TOTAL} />
                </Box>
              </Box>

              {/* ══ CONTENT PANEL ══
                  Fixed height matches photo. overflow:hidden clips the motion.div. */}
              <Flex
                flex={1}
                direction="column"
                justify="space-between"
                position="relative"
                overflow="hidden"
                /* same height as photo on desktop so both panels lock together */
                minH={{ base: "auto", lg: "580px" }}
                p={{ base: 7, md: 10, lg: 14 }}
              >
                {/* animated text block — absolute inside overflow:hidden parent */}
                <Box
                  position={{ base: "relative", lg: "absolute" }}
                  inset={{ lg: 0 }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  p={{ lg: 14 }}
                  pb={{ lg: "140px" }} /* leave room for controls */
                >
                  <AnimatePresence mode="wait" custom={dir} initial={false}>
                    <motion.div
                      key={`text-${idx}`}
                      custom={dir}
                      variants={textVariants}
                      initial="enter"
                      animate="show"
                      exit="exit"
                      transition={{ duration: 0.45, ease }}
                      style={{ willChange: "transform, opacity" }}
                    >
                      <VStack align="flex-start" spacing={1} mb={6}>
                        <Text
                          fontSize="10px" fontWeight="800"
                          letterSpacing="0.22em" textTransform="uppercase"
                          color={member.tagColor}
                        >
                          {member.role}
                        </Text>
                        <Heading
                          fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                          fontWeight="900"
                          fontFamily="Bricolage Grotesque, sans-serif"
                          lineHeight="1.0" letterSpacing="-2px"
                          color={headingCol}
                        >
                          {member.name}
                        </Heading>
                        <Text fontSize="sm" color={textMuted} fontWeight="500">
                          {member.company}
                        </Text>
                        <HStack spacing={1.5} color={textMuted} fontSize="xs" mt={0.5}>
                          <Icon as={FaMapMarkerAlt} boxSize={3} />
                          <Text fontWeight="500">{member.location}</Text>
                        </HStack>
                      </VStack>

                      <Box
                        pl={5} mb={0}
                        style={{ borderLeft: `2px solid ${member.tagColor}45` }}
                      >
                        <Text
                          fontSize={{ base: "md", lg: "lg" }}
                          lineHeight="1.82" color={textBody} fontWeight="400"
                        >
                          {member.story}
                        </Text>
                      </Box>
                    </motion.div>
                  </AnimatePresence>
                </Box>

                {/* ── CONTROLS  (always in flow at bottom) ── */}
                <VStack
                  align="stretch" spacing={4} mt={{ base: 6, lg: 0 }}
                  position={{ base: "relative", lg: "absolute" }}
                  bottom={{ lg: 14 }}
                  left={{ lg: 14 }}
                  right={{ lg: 14 }}
                  zIndex={2}
                >
                  {/* thumb strip — mobile only */}
                  <Box display={{ base: "block", lg: "none" }}>
                    <ThumbStrip members={teamMembers} activeIdx={idx} onSelect={navigate} />
                  </Box>

                  <Flex
                    direction={{ base: "column", sm: "row" }}
                    gap={3}
                    justify="space-between"
                    align={{ base: "stretch", sm: "center" }}
                  >
                    {/* peerlist */}
                    <Link
                      href={member.peerlist} isExternal
                      _hover={{ textDecoration: "none" }}
                      flex={{ sm: 1 }}
                    >
                      <HStack
                        spacing={3} px={6} py={4}
                        rounded="2xl" justify="center"
                        border="1.5px solid" borderColor={btnBorder}
                        transition="all 0.25s ease"
                        _hover={{
                          borderColor: member.tagColor,
                          bg: `${member.tagColor}10`,
                          transform: "translateY(-2px)",
                          shadow: `0 8px 24px ${member.tagColor}22`,
                        }}
                      >
                        <PeerlistIcon boxSize={5} color={headingCol} />
                        <Text fontWeight="700" fontSize="sm" color={headingCol}>
                          Connect on Peerlist
                        </Text>
                      </HStack>
                    </Link>

                    {/* prev / next */}
                    <HStack spacing={2} justify="center">
                      <Box
                        as="button" onClick={prev}
                        w="48px" h="48px" rounded="xl"
                        display="flex" alignItems="center" justifyContent="center"
                        border="1.5px solid" borderColor={btnBorder}
                        color={headingCol}
                        transition="all 0.22s ease"
                        _hover={{ borderColor: "#028EC4", bg: useColorModeValue("gray.50","whiteAlpha.100"), transform: "translateX(-2px)" }}
                        _active={{ transform: "scale(0.9)" }}
                        aria-label="Previous member"
                      >
                        <Icon as={FaArrowLeft} boxSize={3.5} />
                      </Box>

                      <Box
                        as="button" onClick={next}
                        w="48px" h="48px" rounded="xl"
                        display="flex" alignItems="center" justifyContent="center"
                        bg={member.tagColor} color="white"
                        transition="all 0.22s ease"
                        style={{ boxShadow: `0 4px 16px ${member.tagColor}55` }}
                        _hover={{ transform: "translateX(2px)", style: { boxShadow: `0 8px 24px ${member.tagColor}70` } }}
                        _active={{ transform: "scale(0.9)" }}
                        aria-label="Next member"
                      >
                        <Icon as={FaArrowRight} boxSize={3.5} />
                      </Box>
                    </HStack>
                  </Flex>

                  <ProgressBar
                    isPaused={isPaused}
                    duration={AUTO_PLAY_MS}
                    color={member.tagColor}
                    resetKey={`pb-${idx}`}
                  />
                </VStack>
              </Flex>
            </Flex>
          </Box>
        </motion.div>

        {/* ── SWIPE HINT — mobile only ── */}
        <Box
          display={{ base: "flex", lg: "none" }}
          justify="center"
          mt={5}
        >
          <HStack spacing={1.5} opacity={0.45}>
            <Icon as={FaArrowLeft} boxSize={2.5} />
            <Text fontSize="10px" fontWeight="700" letterSpacing="0.15em" textTransform="uppercase">
              Swipe to explore
            </Text>
            <Icon as={FaArrowRight} boxSize={2.5} />
          </HStack>
        </Box>

      </Container>
    </Box>
  );
};

export default TeamSlider;